import { Board } from 'chessboard/model/board/types';
import { Core } from 'chessboard/core/config/types';
import { SimpleStateMachineUtil } from '@chesscom/web-ui';
import { HoverSquare } from 'chessboard/plugins/hover-square/config/types';
import { isNotOneOf } from 'src/utils/app/model/is-not-one-of';
import type { Plugins } from 'chessboard/core/plugins/config/types';
import { StateMachine } from 'chessboard/core/state/config/types';
import { Timer } from 'plugins/clocks/timer/config/types';
import { pipeConditions } from 'src/utils/app/model/pipe-conditions';
import { isOptionEnabled } from 'src/utils/app/model/is-option-enabled';

const { createStateMachine } = SimpleStateMachineUtil;

/**
 * Manages the position and display of the hover square.
 */
export function createHoverSquarePlugin(): Plugins.Model {
  const machine = createStateMachine({
    data: {
      square: undefined,
    },
    initialState: 'hidden',
    states: {
      hidden: {
        dragPiece: (square) => ({ data: { square }, nextState: 'showing' }),
      },
      showing: {
        dragPiece: (square) => ({ data: { square }, nextState: 'showing' }),
        dropPiece: () => ({ nextState: 'hidden' }),
        setOptionToFalse: () => ({ nextState: 'hidden' }),
      },
    },
  });

  return {
    match: [
      {
        condition: pipeConditions(
          isOptionEnabled(Board.BooleanOptions.HoverSquareOutline),
          isNotOneOf([Core.APIEvents.ClearMarkings, Timer.Events.Tick]),
        ),
        handler: updateHoverSquare,
      },
    ],
    name: HoverSquare.Plugin.Name,
  };

  /**
   * Update the hover square.
   */
  function updateHoverSquare(event: Core.Event, context: Core.Context) {
    const { options, renderer, state } = context;

    const draggingPiece =
      state.get(Core.State.HideHoverSquare) !== true &&
      state.get(Core.State.MachineState) !== StateMachine.States.PromotionAreaShowing &&
      state.get(Core.State.DraggingPiece);

    const draggingSquare =
      event.data &&
      (event.data.square ||
        event.data.squareInsideBoard ||
        machine.getData().square ||
        draggingPiece);

    let machineHasTransitioned = false;

    if (!options.highlightMoves) {
      machineHasTransitioned = machine.transition('setOptionToFalse');
    } else if (!draggingPiece) {
      machineHasTransitioned = machine.transition('dropPiece');
    } else {
      machineHasTransitioned = machine.transition('dragPiece', draggingSquare);
    }

    if (!machineHasTransitioned || !renderer) {
      return;
    }

    if (machine.getCurrentState() === 'hidden') {
      renderer.hideHoverSquare(context);
    } else {
      renderer.showHoverSquare(machine.getData().square, context);
    }
  }
}
